import { createFileRoute, redirect } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { type ChangeEvent, useCallback, useMemo, useState } from 'react';
import { z } from 'zod';
import Icon from '@/components/Icon/Icon';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { EditBottomSheet } from '@/features/contract-and-cost/EditBottomSheet';
import { METRIC_ROW_KEYS, useRowVisibility } from '@/features/contract-and-cost/grid';
import { PaymentOverviewFilterBar, type PaymentGroupBy, type PaymentOverviewViewMode, type ComparisonTarget } from '@/features/contract-and-cost/payment';
import { getDefaultTargetMonth, getMonthsForTarget, isValidTargetMonth, parseTargetMonth, targetWindowStartIso } from '@/features/contract-and-cost/targetMonth';
import { PaymentStatCards, PaymentSummaryChart } from '@/features/software/contract-and-cost/components';
import { useEditGridState } from '@/features/software/contract-and-cost/hooks/useEditGridState';
import { MonthlyEditGrid, type MonthlyEditGridDefaultData, type MonthlyEditGridPlan } from '@/features/software/contract-and-cost/MonthlyEditGrid';
import { PaymentPlanOverviewTable, useSoftwarePaymentByDepartmentForSoftware, useSoftwarePaymentByMemberForSoftware, useSoftwarePaymentDetailBySoftwareId, useSoftwarePaymentOverviewBySoftwareId, useSoftwarePaymentSelectablePlansBySoftwareId, useSaveSoftwarePaymentMonthly } from '@/features/software/contract-and-cost/payment';
import { SOFTWARE_PLAN_OVERVIEW_ROW_KEYS, type SoftwarePlanOverviewRowKey } from '@/features/software/contract-and-cost/planOverviewRowVisibility';
import { handleApiError } from '@/lib/api/error';
import { cn } from '@/lib/cn';
import { featureFlags } from '@/lib/env';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { DepartmentPaymentTable } from './-components/DepartmentPaymentTable';
import { MemberPaymentTable } from './-components/MemberPaymentTable';
const SearchSchema = z.object({
  groupBy: z.enum(['catalog', 'member', 'department']).default('catalog'),
  viewMode: z.enum(['monthly', 'transition']).default('transition'),
  month: z.number().min(1).max(12).optional(),
  freeText: z.string().optional(),
  comparisonTarget: z.enum(['PREVIOUS_YEAR', 'PREVIOUS_MONTH']).default('PREVIOUS_MONTH'),
  pageIndex: z.number().int().min(0).default(0),
  pageSize: z.number().int().min(1).max(1000).default(100)
});
export const Route = createFileRoute('/_staff/software/$id/_$id/contract-and-cost/_contract-and-cost/payment/$targetMonth/')({
  component: SoftwarePaymentDetailPage,
  validateSearch: zodValidator(SearchSchema),
  // 不正な対象月（YYYYMM 形式でない / 月が範囲外）は既定の対象月へリダイレクトする。
  beforeLoad: ({
    params
  }) => {
    if (!isValidTargetMonth(params.targetMonth)) {
      throw redirect({
        to: '/software/$id/contract-and-cost/payment/$targetMonth',
        params: {
          id: params.id,
          targetMonth: getDefaultTargetMonth()
        }
      });
    }
  }
});
function SoftwarePaymentDetailPage() {
  const {
    id,
    targetMonth
  } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const {
    i18n
  } = useTranslation(defaultNS);
  const {
    t: tCommon
  } = useTranslation(defaultNS, {
    keyPrefix: 'features.contractAndCost'
  });
  const {
    t: tTable
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.software.contractAndCost.paymentOverview.table'
  });
  // catalog 軸はドメインごとに名称が異なるため明示的に渡す（ソフトウェア =「ソフトウェア別」/ デバイス =「カタログ別」）。
  const {
    t: tGroupBy
  } = useTranslation(defaultNS, {
    keyPrefix: 'features.contractAndCost.overviewFilterBar.groupBy'
  });

  // CONTRACT_AND_COST_NEW_VIEW が無効な場合はカタログ別のみ提供する。
  // 個人別/組織別は非表示にし、URL 直アクセス時も catalog に強制する。
  const isNewViewEnabled = featureFlags.check('CONTRACT_AND_COST_NEW_VIEW');
  const groupBy = isNewViewEnabled ? search.groupBy : 'catalog';
  const groupByOptions: readonly PaymentGroupBy[] = isNewViewEnabled ? ['catalog', 'member', 'department'] : ['catalog'];
  const isCatalog = groupBy === 'catalog';
  const isMember = groupBy === 'member';
  const isDepartment = groupBy === 'department';
  const isMonthly = isCatalog && search.viewMode === 'monthly';
  const monthOptions = useMemo(() => getMonthsForTarget(targetMonth), [targetMonth]);
  const targetYearMonth = useMemo(() => targetWindowStartIso(targetMonth), [targetMonth]);
  // 選択中の対象月（テーブルの列ハイライト用）。
  const highlightMonth = useMemo(() => parseTargetMonth(targetMonth), [targetMonth]);

  // ページングは URL search params を単一の正とする（個人別ビューで使用）。
  const currentPage = search.pageIndex + 1;
  const {
    pageSize
  } = search;
  const {
    toast
  } = useToast();
  const {
    editOpen,
    setEditOpen,
    editGridKey,
    editDataRef,
    isPlanSelected,
    handleEditDataChange,
    openEditor,
    closeEditor
  } = useEditGridState();
  const summaryRegionId = 'software-payment-overview-summary-region';
  const detailQuery = useSoftwarePaymentDetailBySoftwareId(id, targetMonth, search.comparisonTarget, isCatalog);
  const overviewQuery = useSoftwarePaymentOverviewBySoftwareId(id, targetMonth);
  const selectablePlansQuery = useSoftwarePaymentSelectablePlansBySoftwareId(id, isCatalog);
  const memberQuery = useSoftwarePaymentByMemberForSoftware(id, {
    startYearMonth: targetYearMonth,
    durationMonths: monthOptions.length,
    paymentComparisonTarget: search.comparisonTarget,
    page: currentPage,
    pageSize,
    freeText: search.freeText
  }, isMember);
  const departmentDrilldown = useSoftwarePaymentByDepartmentForSoftware(id, {
    startYearMonth: targetYearMonth,
    durationMonths: monthOptions.length,
    paymentComparisonTarget: search.comparisonTarget
  }, {
    page: currentPage,
    pageSize
  }, isDepartment);
  const plans = useMemo<MonthlyEditGridPlan[]>(() => {
    const rows = selectablePlansQuery.data?.planNames ?? [];
    return rows.map(planName => ({
      id: planName,
      label: planName
    }));
  }, [selectablePlansQuery.data]);
  const gridDefaultDataList = useMemo<MonthlyEditGridDefaultData[]>(() => {
    const rows = detailQuery.data?.data ?? [];
    return rows.map(p => ({
      planName: p.planName,
      months: p.months.map(m => ({
        targetYearMonth: m.targetYearMonth,
        plannedAmount: m.plannedPaymentAmount,
        actualAmount: m.actualPaymentAmount,
        userCount: m.userCount
      }))
    }));
  }, [detailQuery.data]);
  const savePaymentMutation = useSaveSoftwarePaymentMonthly(id, monthOptions, {
    onSuccess: () => {
      toast({
        title: tCommon('editBottomSheet.saveSuccessToast')
      });
      closeEditor();
    },
    onError: error => {
      const {
        message
      } = handleApiError(error, i18n);
      toast({
        title: message,
        variant: 'destructive'
      });
    }
  });
  const currentMonth = search.month ?? new Date().getMonth() + 1;

  // 行表示トグル（実績 / 予定 / 予実差異 / 請求モデル / 数量 / 平均単価 / 対象契約）。
  // 親に保持するため groupBy 切替時も表示状態を維持する。個人別 / 組織別ビューは指標 3 行のみ対象。
  const {
    visibility: rowVisibility,
    toggleRow: handleRowVisibilityToggle
  } = useRowVisibility(SOFTWARE_PLAN_OVERVIEW_ROW_KEYS, METRIC_ROW_KEYS);
  const metricRowToggleItems: ReadonlyArray<{
    key: SoftwarePlanOverviewRowKey;
    label: string;
  }> = [{
    key: 'actual',
    label: tTable('rowType.actual')
  }, {
    key: 'scheduled',
    label: tTable('rowType.scheduled')
  }, {
    key: 'variance',
    label: tTable('rowType.variance')
  }];
  const planRowToggleItems: ReadonlyArray<{
    key: SoftwarePlanOverviewRowKey;
    label: string;
  }> = [...metricRowToggleItems, {
    key: 'billingModel',
    label: tTable('rowType.billingModel')
  }, {
    key: 'quantity',
    label: tTable('rowType.quantity')
  }, {
    key: 'averageUnitPrice',
    label: tTable('rowType.averageUnitPrice')
  }, {
    key: 'targetContract',
    label: tTable('rowType.targetContract')
  }];
  const rowToggle = isMonthly ? undefined : {
    items: isCatalog ? planRowToggleItems : metricRowToggleItems,
    visibility: rowVisibility,
    onToggle: handleRowVisibilityToggle,
    requiredKeys: METRIC_ROW_KEYS
  };
  const [showSummary, setShowSummary] = useState(false);
  const handleGroupByChange = useCallback((next: PaymentGroupBy) => {
    navigate({
      search: prev => ({
        ...prev,
        groupBy: next,
        // 個人別/組織別は推移のみ対応のため月選択をクリアする
        viewMode: next === 'catalog' ? prev.viewMode : 'transition',
        month: undefined,
        pageIndex: 0
      }),
      replace: true
    });
  }, [navigate]);
  const handlePageChange = useCallback((page: number) => {
    navigate({
      search: prev => ({
        ...prev,
        pageIndex: page - 1
      }),
      replace: true
    });
  }, [navigate]);
  const handlePageSizeChange = useCallback((size: number) => {
    navigate({
      search: prev => ({
        ...prev,
        pageIndex: 0,
        pageSize: size
      }),
      replace: true
    });
  }, [navigate]);
  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    navigate({
      search: prev => ({
        ...prev,
        freeText: e.target.value || undefined,
        pageIndex: 0
      }),
      replace: true
    });
  }, [navigate]);

  // 個人別ビューのみフルテキスト検索を提供する（カタログ別/組織別は freeText を持たない）。
  const showSearch = isMember;
  return <div className="relative flex flex-1 flex-col gap-5 overflow-auto px-12 py-5 min-h-0">
      <div id={summaryRegionId} hidden={!showSummary}>
        {overviewQuery.data && <VStack className="gap-5">
            <PaymentStatCards summary={overviewQuery.data} />
            <PaymentSummaryChart data={overviewQuery.data.months} months={monthOptions} />
          </VStack>}
      </div>

      <div className="flex justify-end">
        <button type="button" className="flex items-center gap-1 transition-colors" aria-expanded={showSummary} aria-controls={summaryRegionId} onClick={() => setShowSummary(prev => !prev)}>
          <Icon iconName={showSummary ? 'ChevronUpIcon' : 'ChevronDownIcon'} className={cn('size-3.5')} />
          <Text className={cn('text-labelSmallReg', 'text-neutral-muted-on-fill', 'hover:text-neutral-on-fill')}>
            {showSummary ? tCommon('paymentOverview.summary.collapse') : tCommon('paymentOverview.summary.expand')}
          </Text>
        </button>
      </div>

      <PaymentOverviewFilterBar groupBy={groupBy} onGroupByChange={handleGroupByChange} groupByOptions={groupByOptions} groupByLabels={{
      catalog: tGroupBy('software')
    }} viewMode={search.viewMode} onViewModeChange={(mode: PaymentOverviewViewMode) => {
      navigate({
        search: prev => ({
          ...prev,
          viewMode: mode,
          month: mode === 'monthly' ? currentMonth : undefined
        }),
        replace: true
      });
    }} selectedMonth={isMonthly ? currentMonth : undefined} onSelectedMonthChange={(month: number) => {
      navigate({
        search: prev => ({
          ...prev,
          month
        }),
        replace: true
      });
    }} monthOptions={monthOptions} searchValue={showSearch ? search.freeText ?? '' : undefined} onSearchChange={showSearch ? handleSearchChange : undefined} comparisonTarget={search.comparisonTarget} onComparisonTargetChange={(target: ComparisonTarget) => {
      navigate({
        search: prev => ({
          ...prev,
          comparisonTarget: target
        })
      });
    }} rowToggle={rowToggle} />

      {isCatalog && <>
          <HStack className="justify-end">
            <Button variant="outline" size="sm" className="gap-1.5 text-labelSmall" onClick={openEditor}>
              <Icon iconName="PencilIcon" className="size-3.5" />
              {tCommon('editButton')}
            </Button>
          </HStack>

          <EditBottomSheet open={editOpen} onOpenChange={setEditOpen} title={tCommon('editBottomSheet.editPaymentTitle')} onSave={() => {
        const data = editDataRef.current;
        if (!data) return;
        savePaymentMutation.mutate(data);
      }} onCancel={closeEditor} saveDisabled={!isPlanSelected}>
            <MonthlyEditGrid key={editGridKey} months={monthOptions} plans={plans} dataList={gridDefaultDataList} onChange={handleEditDataChange} />
          </EditBottomSheet>
        </>}

      {isMember && <MemberPaymentTable data={memberQuery.data} isPending={memberQuery.isPending} isError={memberQuery.isError} monthList={monthOptions} highlightMonth={highlightMonth} currentPage={currentPage} pageSize={pageSize} onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange} rowVisibility={rowVisibility} className="flex-1 min-h-0" />}

      {isDepartment && <DepartmentPaymentTable rootData={departmentDrilldown.rootData} visibleRows={departmentDrilldown.visibleRows} onToggle={departmentDrilldown.toggle} isPending={departmentDrilldown.isPending} isError={departmentDrilldown.isError} monthList={monthOptions} highlightMonth={highlightMonth} currentPage={currentPage} pageSize={pageSize} onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange} rowVisibility={rowVisibility} className="flex-1 min-h-0" />}

      {isCatalog && !isMonthly && <PaymentPlanOverviewTable detail={detailQuery.data} isPending={detailQuery.isPending} months={monthOptions} highlightMonth={highlightMonth} rowVisibility={rowVisibility} className="flex-1 min-h-0" />}
    </div>;
}